import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { claveWizard, COPY_WIZARD as T, PASOS, primerPendiente, wizardActivo } from "../lib/wizard";
import { Senales } from "../components/PrimerosPasos";
import Doctors from "./Doctors";
import AssistantConfig from "./AssistantConfig";

export function PruebaSofi({ onRespuesta, puedeOperar }) {
  const [mensaje, setMensaje] = useState("");
  const [turnos, setTurnos] = useState([]);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState(null);
  const enviar = async e => {
    e.preventDefault();
    if (ocupado || !puedeOperar || !mensaje.trim()) return;
    setOcupado(true); setError(null);
    try {
      const mensajes = turnos.slice(-4).flatMap(t => [
        { rol: "user", texto: t.consulta.slice(0, 1000) }, { rol: "assistant", texto: t.respuesta.slice(0, 1000) },
      ]);
      mensajes.push({ rol: "user", texto: mensaje.trim() });
      const { data } = await api.post("/sofi/vista-previa", { mensajes });
      if (data?.ok !== true || typeof data.respuesta !== "string" || !data.respuesta.trim()) throw new Error();
      setTurnos(prev => [...prev, { consulta: mensaje, respuesta: data.respuesta }]);
      setMensaje("");
      await onRespuesta();
    } catch (err) { setError(err?.response?.status === 429 ? T.limite : T.error); }
    finally { setOcupado(false); }
  };
  return <section className="rounded-xl border bg-white p-5 space-y-4">
    <h2 className="text-xl font-bold">Prueba a Sofi</h2>
    <p>{T.aviso}</p>
    <div aria-live="polite" className="space-y-4">{turnos.map((t, i) => <div key={i}>
      <p className="text-right whitespace-pre-wrap">{t.consulta}</p><p className="bg-slate-50 p-3 whitespace-pre-wrap">{t.respuesta}</p>
    </div>)}</div>
    <form onSubmit={enviar} className="flex gap-3">
      <label className="flex-1">{T.consulta}<textarea className="block w-full border rounded p-3" value={mensaje}
        onChange={e => setMensaje(e.target.value)} maxLength={1000} disabled={ocupado || !puedeOperar} /></label>
      <button className="self-end rounded bg-blue-600 text-white p-3 disabled:opacity-50" disabled={ocupado || !puedeOperar || !mensaje.trim()}>{T.enviar}</button>
    </form>
    {!puedeOperar && <p>{T.pendiente}</p>}
    {error && <p role="alert">{error}</p>}
  </section>;
}

export default function Configurar() {
  const { user, puede } = useAuth();
  const nav = useNavigate();
  const editor = useRef(null);
  const [datos, setDatos] = useState(null);
  const [paso, setPaso] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [fallo, setFallo] = useState(false);
  const permitidos = PASOS.filter(p => puede(p.cap));
  const cid = user?.active_clinic_id;
  const activo = wizardActivo(user);
  const clave = claveWizard(user, "paso");
  const cargar = useCallback(async () => {
    if (!cid) throw new Error("NO_ACTIVE_CLINIC");
    const { data } = await api.get("/primeros-pasos");
    setDatos(data); setFallo(false); return data;
  }, [cid]);
  useEffect(() => {
    if (!activo) return;
    let vivo = true;
    setDatos(null); setPaso(null);
    cargar().then(data => {
      if (!vivo) return;
      const guardado = localStorage.getItem(clave);
      setPaso(permitidos.some(p => p.id === guardado) ? guardado : primerPendiente(data.pasos, permitidos));
      localStorage.setItem(claveWizard(user, "visto"), "1");
    }).catch(() => { if (vivo) setFallo(true); });
    const volver = () => cargar().catch(() => { setFallo(true); setDatos(null); });
    window.addEventListener("focus", volver);
    return () => { vivo = false; window.removeEventListener("focus", volver); };
  // Cada clínica se remonta también por la key de AppLayout.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activo, cid, clave]);
  if (!activo) return <Navigate to="/app" replace />;
  const indice = permitidos.findIndex(p => p.id === paso);
  const elegir = id => { localStorage.setItem(clave, id); setPaso(id); };
  const guardar = async accion => {
    if (ocupado) return;
    setOcupado(true);
    try {
      if (editor.current && await editor.current.guardar() !== true) return;
      if (accion === "salir") { nav("/app"); return; }
      const actual = await cargar();
      if (accion.startsWith("paso:")) { elegir(accion.slice(5)); return; }
      if (accion === "anterior" && indice > 0) elegir(permitidos[indice - 1].id);
      else if (actual.pasos[paso] === true && indice < permitidos.length - 1) elegir(permitidos[indice + 1].id);
    } catch { setFallo(true); setDatos(null); }
    finally { setOcupado(false); }
  };
  const operacion = user?.plan_vigente?.puede_operar !== false && user?.plan_vigente?.vigente !== false;
  return <div className="p-6 max-w-6xl mx-auto space-y-6">
    <h1 className="text-3xl font-bold">{T.titulo}</h1>
    <Senales datos={datos} permitidos={permitidos} />
    <progress aria-label={T.titulo} max={permitidos.length} value={permitidos.filter(p => datos?.pasos?.[p.id] === true).length} className="w-full" />
    <nav aria-label={T.titulo} className="flex flex-wrap gap-3">{permitidos.map(p => <button disabled={ocupado} key={p.id}
      className={`rounded-lg border px-4 py-2 ${p.id === paso ? "border-blue-600 text-blue-700" : "border-slate-200"}`}
      aria-current={p.id === paso ? "step" : undefined} onClick={() => guardar("paso:" + p.id)}>{p.label}</button>)}</nav>
    {fallo && <p role="alert">{T.pendiente} <button onClick={() => cargar().then(d => { if (!paso) elegir(primerPendiente(d.pasos, permitidos)); }).catch(() => setFallo(true))}>{T.retomar}</button></p>}
    {datos && paso && <>
      {["medicos", "servicios", "horarios"].includes(paso) && <Doctors key={paso} ref={editor} wizardPaso={paso} incompletos={datos.medicos_incompletos?.[paso]} onSaved={cargar} onContinue={() => guardar("continuar")} onExit={() => guardar("salir")} />}
      {["sofi", "whatsapp"].includes(paso) && <AssistantConfig key={paso} ref={editor} wizardPaso={paso} onSaved={cargar} />}
      {paso === "prueba" && <PruebaSofi onRespuesta={cargar} puedeOperar={operacion} />}
    </>}
    {datos?.completo && <p role="status">{T.final}</p>}
    <footer className="flex flex-wrap gap-3">
      <button className="rounded border p-3" disabled={ocupado || indice <= 0} onClick={() => guardar("anterior")}>{T.anterior}</button>
      <button className="rounded bg-blue-600 text-white p-3" disabled={ocupado || !datos} onClick={() => guardar("continuar")}>{T.continuar}</button>
      <button className="rounded border p-3" disabled={ocupado} onClick={() => guardar("salir")}>{T.salir}</button>
    </footer>
  </div>;
}
